import { useEffect } from "react";
import { ArrowRight, X } from "lucide-react";
import {
  chatWhatsappLinkForCountry,
  countryDetails,
  supportedCountries,
} from "../../../src/countries.ts";

export type CountryPickerModalProps = {
  isOpen: boolean;
  onClose: () => void;
};

export const CountryPickerModal = ({
  isOpen,
  onClose,
}: CountryPickerModalProps) => {
  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e: Event) => {
      if ("key" in e && e.key === "Escape") onClose();
    };
    addEventListener("keydown", handleKeyDown);
    return () => removeEventListener("keydown", handleKeyDown);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/75 backdrop-blur-md animate-fade-in"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="country-picker-title"
    >
      <div
        className="glass-card relative w-full max-w-lg p-6 sm:p-8 rounded-3xl border border-white/15 shadow-2xl bg-[#141414]/95 text-left"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between mb-4">
          <div>
            <h2
              id="country-picker-title"
              className="text-xl sm:text-2xl font-bold text-white tracking-tight"
            >
              Choose your country
            </h2>
            <p className="text-sm text-gray-400 mt-1">
              Connect with Agent FOMO on WhatsApp in your region
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 text-gray-400 hover:text-white rounded-xl bg-white/5 hover:bg-white/10 transition"
            aria-label="Close"
          >
            <X size={20} />
          </button>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4 mt-6">
          {supportedCountries.map((c) => {
            const details = countryDetails[c];
            return (
              <a
                key={c}
                href={chatWhatsappLinkForCountry(c)}
                target="_blank"
                rel="noopener noreferrer"
                onClick={onClose}
                className="flex sm:flex-col items-center sm:justify-center p-4 sm:p-5 rounded-2xl bg-white/[0.04] hover:bg-purple-600/15 border border-white/10 hover:border-purple-500/50 transition-all duration-200 hover:scale-[1.03] group shadow-lg"
              >
                <span
                  className="text-4xl sm:text-5xl mb-0 sm:mb-3 mr-3.5 sm:mr-0 group-hover:scale-110 transition-transform select-none"
                  role="img"
                  aria-label={c}
                >
                  {details.flag}
                </span>
                <div className="flex-1 sm:flex-initial text-left sm:text-center min-w-0">
                  <div className="text-base sm:text-lg font-semibold text-white group-hover:text-purple-300 transition-colors">
                    {c}
                  </div>
                  <div className="text-xs text-gray-400 mt-0.5 truncate">
                    {details.cities.length > 0
                      ? details.cities.join(", ")
                      : "Nationwide"}
                  </div>
                </div>
                <ArrowRight className="w-4 h-4 text-gray-500 group-hover:text-purple-400 group-hover:translate-x-0.5 transition-all sm:hidden shrink-0 ml-2" />
              </a>
            );
          })}
        </div>
      </div>
    </div>
  );
};
